import { Alert, Box, Group, Paper, SimpleGrid, Skeleton, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import type { Activity, Content, News } from '~api';
import { formatDate } from '~lib';
import { BrandLogo } from '~portal/components/BrandLogo';
import {
  AnchorLink,
  ArrowMark,
  Delayed,
  IllustrativeBadge,
  PaperLink,
  UnstyledButtonLink,
} from '~portal/components/common';
import { type LiveQuery, type RegionPages, useRegionPages } from '~portal/data';
import { type UpdateRow, updateRows } from '~portal/data/home';
import { excerptOf, newsDestination } from '~portal/data/news';
import * as styles from './home.css';
import { subjectRoute } from './links';

/** @desc The viewer's local clock time, `14:05`; the date beside it is `formatDate`'s. */
export const formatTime = (at: Date): string =>
  new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    at,
  );

/** @desc "See more…": the section's full listing (UI round 3, H2-310), named for screen readers. */
function SeeMore({ to, what }: { to: '/news' | '/updates'; what: string }) {
  return (
    <AnchorLink to={to} size="sm" aria-label={`See more ${what}`}>
      See more…
    </AnchorLink>
  );
}

function LoadError({ title, message }: { title: string; message: string }) {
  return (
    <Alert variant="light" color="red" icon={<IconAlertCircle size={18} />} title={title}>
      {message}
    </Alert>
  );
}

export type RecentUpdatesProps = {
  note: LiveQuery<Content.AnalystNote | null>;
  events: LiveQuery<readonly Activity.Event[]>;
};

/**
 * @desc Recent updates (H4, Figma `426:7806`): a full-width four-row table — type, entity, time
 * over date, an arrow — each row one link to its page. The latest analyst note is the highlighted
 * first row (D-2). Events come from `activity.recent` (new stories, the caller's own follows);
 * only while that feed is empty do the fixture events stand in, badged. Loading and failure are
 * shown as such, never papered over. "See more…" opens `/updates`.
 */
export function RecentUpdates({ note, events }: RecentUpdatesProps) {
  const pending = note.isLoading || events.isLoading;
  const error = note.error ?? events.error;
  const regions = useRegionPages();
  const table = !pending && !error ? updateRows(note.data, events.data ?? []) : null;

  return (
    <Stack gap="sm" component="section" aria-labelledby="home-updates">
      <Group justify="space-between" align="center" gap="sm" wrap="wrap">
        <Group gap="xs" align="center">
          <Title order={2} id="home-updates">
            Recent updates
          </Title>
          {table?.fixtureEvents && <IllustrativeBadge label="Illustrative events" />}
        </Group>
        <SeeMore to="/updates" what="updates" />
      </Group>
      <Paper withBorder className={styles.clipCard} data-testid="home-updates-table">
        {pending && (
          <Delayed>
            <Stack gap="sm" p="md" aria-busy data-testid="home-updates-loading">
              <Skeleton height={24} />
              <Skeleton height={24} />
              <Skeleton height={24} />
              <Skeleton height={24} />
            </Stack>
          </Delayed>
        )}
        {error && (
          <Box p="md">
            <LoadError title="Recent updates could not be loaded" message={error.message} />
          </Box>
        )}
        {table && table.rows.length === 0 && (
          <Text c="dimmed" ta="center" p="xl">
            No updates yet
          </Text>
        )}
        {table && table.rows.length > 0 && (
          <Box component="ul" className={styles.bareRows} aria-label="Recent updates">
            {table.rows.map((row) => (
              <li key={row.id}>
                <UpdateRowLink row={row} regions={regions.data} />
              </li>
            ))}
          </Box>
        )}
      </Paper>
    </Stack>
  );
}

/**
 * @desc One update row as a link to its page — Home's table and the `/updates` listing. A region
 * row links only once `regions` says the page exists (`useRegionPages`); until then, and for a
 * region the portal has no page for, it is the same row without the arrow rather than a link to
 * "Region not found".
 */
export function UpdateRowLink({
  row,
  regions,
}: {
  row: UpdateRow;
  regions: Pick<RegionPages, 'get'> | undefined;
}) {
  const className = [styles.updateRow, row.highlighted ? styles.updateRowHighlighted : '']
    .filter(Boolean)
    .join(' ');
  const linked = row.subject.kind === 'operator' || regions?.get(row.subject.slug) !== undefined;
  const body = (
    <>
      <Group gap="xs" wrap="wrap" align="center">
        <Text fw={600}>{row.typeLabel}</Text>
        {row.illustrative && <IllustrativeBadge label="Illustrative" />}
      </Group>
      <Box miw={0} className={styles.updateCellWide}>
        <Text truncate>{row.entityLabel}</Text>
        {row.detail && (
          <Text size="sm" c="dimmed" truncate>
            {row.detail}
          </Text>
        )}
      </Box>
      <Box className={styles.updateCellWide} ta="right">
        <Text size="sm">{formatTime(row.occurredAt)}</Text>
        <Text size="sm" c="dimmed">
          {formatDate(row.occurredAt)}
        </Text>
      </Box>
      {linked && <ArrowMark />}
    </>
  );
  if (!linked) {
    return (
      <Box
        className={className}
        data-highlighted={row.highlighted || undefined}
        data-testid="update-row-unlinked"
      >
        {body}
      </Box>
    );
  }
  return (
    <UnstyledButtonLink
      to={subjectRoute(row.subject)}
      params={{ slug: row.subject.slug }}
      className={className}
      aria-label={`${row.typeLabel}: ${row.entityLabel}`}
      data-highlighted={row.highlighted || undefined}
    >
      {body}
    </UnstyledButtonLink>
  );
}

/**
 * @desc A story's cover (H5): its image when it has one, else the designed default — the H2 mark on
 * brand navy. Legacy stories carry no image, so the default is what the demo shows.
 */
export function NewsCover({ imageUrl, title }: { imageUrl?: string; title: string }) {
  if (imageUrl) return <img src={imageUrl} alt="" className={styles.coverImage} loading="lazy" />;
  return (
    <Box className={styles.defaultCover} aria-hidden data-testid="news-default-cover" title={title}>
      <BrandLogo height={48} />
    </Box>
  );
}

function NewsCardBody({ item }: { item: News.Item }) {
  const excerpt = excerptOf(item);
  return (
    <>
      <NewsCover imageUrl={item.imageUrl} title={item.title} />
      <Stack gap="xs" p="md" flex={1}>
        <Text size="sm" c="dimmed">
          {formatDate(item.publishedAt)} · {item.source}
        </Text>
        <Text fw={600} fz="lg" lineClamp={2} component="h3" m={0}>
          {item.title}
        </Text>
        {excerpt && (
          <Text size="sm" lineClamp={3}>
            {excerpt}
          </Text>
        )}
        <Group justify="flex-end" mt="auto">
          <ArrowMark />
        </Group>
      </Stack>
    </>
  );
}

/** @desc One news card, a single link to its destination; a story with none is a plain article. */
function NewsCard({ item, regions }: { item: News.Item; regions: RegionPages | undefined }) {
  const destination = newsDestination(item, regions);
  const common = {
    className: styles.newsCard,
    withBorder: true,
    'aria-label': item.title,
  } as const;
  switch (destination.kind) {
    case 'external':
      return (
        <Paper
          component="a"
          href={destination.href}
          target="_blank"
          rel="noopener noreferrer"
          {...common}
        >
          <NewsCardBody item={item} />
        </Paper>
      );
    case 'operator':
    case 'region':
      return (
        <PaperLink
          to={destination.kind === 'operator' ? '/operators/$slug' : '/regions/$slug'}
          params={{ slug: destination.slug }}
          {...common}
        >
          <NewsCardBody item={item} />
        </PaperLink>
      );
    case 'none':
      return (
        <Paper component="article" {...common}>
          <NewsCardBody item={item} />
        </Paper>
      );
  }
}

/**
 * @desc Recent news (H5): the four latest live stories as a 2×2 grid of cards — 140px cover, date
 * eyebrow, two-line title, three-line excerpt, arrow. Live from the legacy backfill, so no badge;
 * a headline repeated on one day arrives collapsed (H2-310). A story's market is linked only where
 * the portal has that page (`newsDestination`). "See more…" opens `/news`.
 */
export function RecentNews({ news }: { news: LiveQuery<readonly News.Item[]> }) {
  const regions = useRegionPages();
  return (
    <Stack gap="sm" component="section" aria-labelledby="home-news">
      <Group justify="space-between" align="center" gap="sm" wrap="wrap">
        <Title order={2} id="home-news">
          Recent news
        </Title>
        <SeeMore to="/news" what="news" />
      </Group>
      {news.isLoading && (
        <Delayed>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" data-testid="home-news-loading">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height={300} radius="lg" />
            ))}
          </SimpleGrid>
        </Delayed>
      )}
      {news.error && <LoadError title="News could not be loaded" message={news.error.message} />}
      {news.data && news.data.length === 0 && (
        <Text c="dimmed" ta="center" p="xl">
          No news yet
        </Text>
      )}
      {news.data && news.data.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          {news.data.map((item) => (
            <NewsCard key={item.id} item={item} regions={regions.data} />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
